import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  AvatarGroup,
  Chip,
  Badge,
  Button,
  ButtonGroup,
  CircularProgress
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
  SportsEsports as GameIcon,
  CheckCircle as ApprovedIcon,
  Cancel as PendingIcon,
  Close as CloseIcon,
  TrendingUp as RemontadaIcon,
  LocalFireDepartment as PalizaIcon,
  Extension as ComboIcon,
  Shield as ResilienciaIcon,
  Person as SobrevivienteIcon,
  Bolt as RapidaIcon,
  WatchLater as WatchLaterIcon,
  Close as RejectedIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../services/api';
import RaceChip from './RaceChip';
import { AuthContext } from '../context/AuthContext';
import useMatchActions from '../hooks/useMatchActions';
import { useNavigate } from 'react-router-dom';

const getTierColor = (tier) => {
  switch (tier) {
    case 'Amistoso':
      return { bg: '#4caf50', text: '#fff' };
    case 'Amistoso c/ Arbitro':
      return { bg: '#2196f3', text: '#fff' };
    case 'Arena':
      return { bg: '#ff9800', text: '#fff' };
    case 'Torneo':
      return { bg: '#f44336', text: '#fff' };
    default:
      return { bg: 'action.hover', text: 'text.primary' };
  }
};

const getHighlightIcon = (highlight) => {
  switch (highlight) {
    case 'Remontada':
      return <RemontadaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Paliza':
      return <PalizaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Combo':
      return <ComboIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Resiliencia':
      return <ResilienciaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Sobreviviente':
      return <SobrevivienteIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Rapida':
      return <RapidaIcon sx={{ fontSize: '1.2rem' }} />;
    default:
      return null;
  }
};

const DenyMatchCard = ({ match, onUpdate, onClick, onCreateNewSuccess }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [decksMap, setDecksMap] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { loading, handleCancel, handleCreateNewFromRejected } = useMatchActions(onUpdate);

  useEffect(() => {
    const fetchDecks = async () => {
      try {
        if (!match?.team1?.decks || !match?.team2?.decks) {
          console.log('No hay mazos para cargar');
          return;
        }

        // Obtener todos los IDs de mazos únicos
        const deckIds = new Set();
        if (match.team1?.decks) {
          match.team1.decks.forEach(deck => deckIds.add(deck.deck_id));
        }
        if (match.team2?.decks) {
          match.team2.decks.forEach(deck => deckIds.add(deck.deck_id));
        }

        if (deckIds.size === 0) {
          console.log('No hay IDs de mazos para cargar');
          return;
        }

        // Obtener la información de todos los mazos
        const response = await api.get('/api/decks');
        const allDecks = response.data;
        
        // Crear un mapa de ID a información del mazo
        const newDecksMap = {};
        allDecks.forEach(deck => {
          if (deckIds.has(deck.id)) {
            newDecksMap[deck.id] = deck;
          }
        });

        // Marcar mazos no encontrados como eliminados
        deckIds.forEach(deckId => {
          if (!newDecksMap[deckId]) {
            newDecksMap[deckId] = {
              id: deckId,
              name: `Mazo Eliminado (ID: ${deckId})`,
              raza: 'Desconocida'
            };
          }
        });
        
        setDecksMap(newDecksMap);
      } catch (error) {
        console.error('Error fetching decks:', error);
        // Inicializar con mazos eliminados en caso de error
        const errorDecksMap = {};
        if (match?.team1?.decks) {
          match.team1.decks.forEach(deck => {
            errorDecksMap[deck.deck_id] = {
              id: deck.deck_id,
              name: `Mazo Eliminado (ID: ${deck.deck_id})`,
              raza: 'Desconocida'
            };
          });
        }
        if (match?.team2?.decks) {
          match.team2.decks.forEach(deck => {
            errorDecksMap[deck.deck_id] = {
              id: deck.deck_id,
              name: `Mazo Eliminado (ID: ${deck.deck_id})`,
              raza: 'Desconocida'
            };
          });
        }
        setDecksMap(errorDecksMap);
      }
    };

    if (match) {
      fetchDecks();
    }
  }, [match]);

  const getPlayerDecks = (teamNumber) => {
    try {
      const team = teamNumber === 1 ? match?.team1 : match?.team2;
      if (!team?.decks) return [];
      
      return team.decks.map(deck => {
        if (!deck) {
          return {
            name: 'Mazo no disponible',
            race: 'Desconocida'
          };
        }
        
        if (deck.deck_raza) {
          return {
            name: deck.deck_name || 'Mazo sin nombre',
            race: deck.deck_raza
          };
        }
        
        const deckInfo = decksMap[deck.deck_id];
        if (deckInfo) {
          return {
            name: deckInfo.name || 'Mazo sin nombre',
            race: deckInfo.raza
          };
        }
        
        return {
          name: `Mazo Eliminado (ID: ${deck.deck_id})`,
          race: 'Desconocida'
        };
      }).filter(Boolean);
    } catch (error) {
      console.error('Error getting player decks:', error);
      return [];
    }
  };

  const team1Players = match.team1?.players || [];
  const team2Players = match.team2?.players || [];
  const isTeam1Winner = match.winning_team === 'team1';

  const handleClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      await onClick(e, match);
    }
  }, [onClick, match]);

  const handleOk = async (e) => {
    e.stopPropagation();
    setIsProcessing(true);
    try {
      await handleCancel(match.id);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error al cancelar la partida:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateNew = async (e) => {
    e.stopPropagation();
    setIsProcessing(true);
    try {
      await handleCreateNewFromRejected(match);
      // After creating new, also cancel the original match
      await handleCancel(match.id);
      if (onCreateNewSuccess) {
        onCreateNewSuccess();
      }
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('❌ DenyMatchCard - Error al crear nueva partida:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!match) {
    return null;
  }

  if (isProcessing) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.8,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(244, 67, 54, 0.5)',
          bgcolor: 'background.paper',
          transition: 'all 0.2s',
          position: 'relative',
          minHeight: '200px',
          opacity: 0.7,
          pointerEvents: 'none'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 2,
            zIndex: 1
          }}
        >
          <CircularProgress size={40} sx={{ color: 'rgba(244, 67, 54, 0.8)' }} />
        </Box>
        <Stack spacing={1.5} sx={{ filter: 'blur(1px)' }}>
          {/* Match Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: 0.8,
            flexWrap: 'wrap'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
            }}>
              <ScheduleIcon sx={{ 
                color: 'text.secondary',
                fontSize: '1.26rem'
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.84rem'
                }} 
                noWrap
              >
                {dayjs(match.match_date).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.8,
              ml: 'auto'
            }}>
              {match.match_mode && typeof match.match_mode === 'string' && (
                <Chip
                  icon={<GameIcon sx={{ 
                    color: 'text.secondary',
                    fontSize: '1.26rem'
                  }} />}
                  label={match.match_mode}
                  size="small"
                  sx={{ 
                    height: '23px',
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    '& .MuiChip-label': { 
                      fontSize: '0.735rem',
                      px: 0.8
                    }
                  }}
                />
              )}
              {match.tier && typeof match.tier === 'string' && (
                <Chip
                  icon={<TrophyIcon sx={{ 
                    color: getTierColor(match.tier).text,
                    fontSize: '1.2rem'
                  }} />}
                    label={match.tier}
                    size="small"
                    sx={{ 
                      height: '22px',
                      bgcolor: getTierColor(match.tier).bg,
                      color: getTierColor(match.tier).text,
                      '& .MuiChip-label': { 
                        fontSize: '0.7rem',
                        px: 0.8
                      }
                    }}
                  />
                )}
            </Box>
          </Box>

          {/* Teams Section */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            py: 0.8
          }}>
            {/* Team 1 */}
            <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1,
                width: '100%',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: isTeam1Winner ? '#1976d2' : 'text.secondary',
                    fontSize: { xs: '1.8rem', sm: '2.2rem' },
                    minWidth: '60px',
                    textAlign: 'center',
                    flexShrink: 0
                  }}
                >
                  {match.team1?.score || 0}
                </Typography>
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mr: 0,
                  minWidth: 0,
                  width: '100%'
                }}>
                  <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0,
                      '& .MuiBadge-root': {
                        marginLeft: '-8px',
                        '&:first-of-type': {
                          marginLeft: 0
                        }
                      }
                    }}>
                      {team1Players.map((player, idx) => (
                        <Badge
                          key={idx}
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            player.approval_status === 'rejected' ? (
                              <Box
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px solid #f44336'
                                }}
                              >
                                <RejectedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: '#f44336',
                                  }}
                                />
                              </Box>
                            ) : null
                          }
                        >
                          <Avatar
                            sx={{
                              width: 26.8,
                              height: 26.8,
                              fontSize: '0.76rem',
                              bgcolor: isTeam1Winner ? '#1976d2' : 'grey.700',
                              border: '2px solid',
                              borderColor: player.approval_status === 'rejected' ? '#f44336' : (isTeam1Winner ? '#1976d2' : 'grey.500'),
                              color: 'white'
                            }}
                          >
                            {player.username?.[0]?.toUpperCase() || '?'}
                          </Avatar>
                        </Badge>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={isTeam1Winner ? 'bold' : 'normal'}
                        sx={{ 
                          fontSize: { xs: '0.735rem', sm: '0.86rem' },
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: isTeam1Winner ? '#1976d2' : 'text.secondary'
                        }}
                      >
                        {team1Players.map((player, idx) => (
                          <React.Fragment key={idx}>
                            {player.username}{idx < team1Players.length - 1 ? ', ' : ''}
                          </React.Fragment>
                        ))}
                      </Typography>
                      {getPlayerDecks(1).map((deck, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <RaceChip race={deck.race} size="small" />
                        </Box>
                      ))}
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Stack>

            {/* VS */}
            <Typography 
              variant="body1" 
              sx={{ 
                mx: 1,
                fontSize: { xs: '0.86rem', sm: '0.99rem' },
                color: 'text.secondary',
                fontWeight: 'bold'
              }}
            >
              VS
            </Typography>

            {/* Team 2 */}
            <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1,
                width: '100%',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: !isTeam1Winner ? '#1976d2' : 'text.secondary',
                    fontSize: { xs: '1.8rem', sm: '2.2rem' },
                    minWidth: '60px',
                    textAlign: 'center',
                    flexShrink: 0,
                    order: 2
                  }}
                >
                  {match.team2?.score || 0}
                </Typography>
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: 0,
                  width: '100%',
                  order: 1
                }}>
                  <Stack spacing={0.5} alignItems="center">
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0,
                      '& .MuiBadge-root': {
                        marginLeft: '-8px',
                        '&:first-of-type': {
                          marginLeft: 0
                        }
                      }
                    }}>
                      {team2Players.map((player, idx) => (
                        <Badge
                          key={idx}
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            player.approval_status === 'rejected' ? (
                              <Box
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px solid #f44336'
                                }}
                              >
                                <RejectedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: '#f44336',
                                  }}
                                />
                              </Box>
                            ) : null
                          }
                        >
                          <Avatar
                            sx={{
                              width: 26.8,
                              height: 26.8,
                              fontSize: '0.76rem',
                              bgcolor: !isTeam1Winner ? '#1976d2' : 'grey.700',
                              border: '2px solid',
                              borderColor: player.approval_status === 'rejected' ? '#f44336' : (!isTeam1Winner ? '#1976d2' : 'grey.500'),
                              color: 'white'
                            }}
                          >
                            {player.username?.[0]?.toUpperCase() || '?'}
                          </Avatar>
                        </Badge>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={!isTeam1Winner ? 'bold' : 'normal'}
                        sx={{ 
                          fontSize: { xs: '0.735rem', sm: '0.86rem' },
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: !isTeam1Winner ? '#1976d2' : 'text.secondary'
                        }}
                      >
                        {team2Players.map((player, idx) => (
                          <React.Fragment key={idx}>
                            {player.username}{idx < team2Players.length - 1 ? ', ' : ''}
                          </React.Fragment>
                        ))}
                      </Typography>
                      {getPlayerDecks(2).map((deck, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <RaceChip race={deck.race} size="small" />
                        </Box>
                      ))}
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Match Info - Highlights */}
          {(() => {
            try {
              if (!match.highlights) return null;
              const highlights = Array.isArray(match.highlights) ? 
                match.highlights : 
                JSON.parse(match.highlights);

              if (Array.isArray(highlights) && highlights.length > 0) {
                return (
                  <Stack 
                    direction="row" 
                    spacing={0.4}
                    justifyContent="center"
                    flexWrap="wrap"
                    sx={{ gap: 0.4 }}
                  >
                    {highlights.map((highlight, index) => {
                      // Asegurar que highlight es un string
                      const highlightText = typeof highlight === 'string' ? highlight :
                        typeof highlight === 'object' && highlight.description ? highlight.description :
                        'Highlight desconocido';
                      
                      return (
                        <Chip
                          key={index}
                          icon={getHighlightIcon(highlightText)}
                          label={highlightText}
                          size="small"
                          sx={{ 
                            height: '22px',
                            bgcolor: '#0277bd',
                            color: 'white',
                            '& .MuiChip-label': { 
                              fontSize: '0.7rem',
                              px: 0.8
                            },
                            '& .MuiChip-icon': {
                              color: 'white',
                              fontSize: '1.2rem'
                            }
                          }}
                        />
                      );
                    })}
                  </Stack>
                );
              }
              return null;
            } catch (error) {
              console.warn('Error parsing highlights:', error);
              return null;
            }
          })()}

          {/* Action Buttons */}
          <ButtonGroup 
            variant="contained" 
            sx={{ 
              width: '100%', 
              mt: 1,
              '& .MuiButton-root': {
                flex: 1,
                textTransform: 'none',
                borderRadius: 1.5,
                px: 1,
                minWidth: 0
              }
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Button
              color="success"
              startIcon={<ApprovedIcon />}
              onClick={handleOk}
              disabled={loading}
              sx={{ 
                pointerEvents: 'auto',
                '& .MuiButton-startIcon': {
                  marginRight: '2px'
                },
                '& .MuiButton-label': {
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Aceptar Rechazo'
              )}
            </Button>
            <Button
              color="primary"
              startIcon={<GameIcon />}
              onClick={handleCreateNew}
              disabled={loading}
              sx={{ pointerEvents: 'auto' }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Crear Nueva'
              )}
            </Button>
          </ButtonGroup>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      onClick={handleClick}
      sx={{
        p: 1.8,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(244, 67, 54, 0.5)',
        bgcolor: 'background.paper',
        transition: 'all 0.2s',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'rgba(244, 67, 54, 1)',
          boxShadow: '0 4px 8px rgba(244, 67, 54, 0.07)'
        }
      }}
    >
      <Stack spacing={1.5}>
        {/* Match Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start',
          gap: 0.8,
          flexWrap: 'wrap'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5,
          }}>
            <ScheduleIcon sx={{ 
              color: 'text.secondary',
              fontSize: '1.26rem'
            }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.84rem'
              }} 
              noWrap
            >
              {dayjs(match.match_date).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.8,
            ml: 'auto'
          }}>
            {match.match_mode && typeof match.match_mode === 'string' && (
              <Chip
                icon={<GameIcon sx={{ 
                  color: 'text.secondary',
                  fontSize: '1.26rem'
                }} />}
                label={match.match_mode}
                size="small"
                sx={{ 
                  height: '23px',
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '& .MuiChip-label': { 
                    fontSize: '0.735rem',
                    px: 0.8
                  }
                }}
              />
            )}
            {match.tier && typeof match.tier === 'string' && (
              <Chip
                icon={<TrophyIcon sx={{ 
                  color: getTierColor(match.tier).text,
                  fontSize: '1.2rem'
                }} />}
                  label={match.tier}
                  size="small"
                  sx={{ 
                    height: '22px',
                    bgcolor: getTierColor(match.tier).bg,
                    color: getTierColor(match.tier).text,
                    '& .MuiChip-label': { 
                      fontSize: '0.7rem',
                      px: 0.8
                    }
                  }}
                />
              )}
          </Box>
        </Box>

        {/* Teams Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 0.8
        }}>
          {/* Team 1 */}
          <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              justifyContent: 'center'
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: isTeam1Winner ? '#1976d2' : 'text.secondary',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' },
                  minWidth: '60px',
                  textAlign: 'center',
                  flexShrink: 0
                }}
              >
                {match.team1?.score || 0}
              </Typography>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mr: 0,
                minWidth: 0,
                width: '100%'
              }}>
                <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0,
                    '& .MuiBadge-root': {
                      marginLeft: '-8px',
                      '&:first-of-type': {
                        marginLeft: 0
                      }
                    }
                  }}>
                    {team1Players.map((player, idx) => (
                      <Badge
                        key={idx}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          player.approval_status === 'rejected' ? (
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #f44336'
                              }}
                            >
                              <RejectedIcon
                                sx={{
                                  fontSize: 14,
                                  color: '#f44336',
                                }}
                              />
                            </Box>
                          ) : null
                        }
                      >
                        <Avatar
                          sx={{
                            width: 26.8,
                            height: 26.8,
                            fontSize: '0.76rem',
                            bgcolor: isTeam1Winner ? '#1976d2' : 'grey.700',
                            border: '2px solid',
                            borderColor: player.approval_status === 'rejected' ? '#f44336' : (isTeam1Winner ? '#1976d2' : 'grey.500'),
                            color: 'white'
                          }}
                        >
                          {player.username?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                      </Badge>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={isTeam1Winner ? 'bold' : 'normal'}
                      sx={{ 
                        fontSize: { xs: '0.735rem', sm: '0.86rem' },
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: isTeam1Winner ? '#1976d2' : 'text.secondary'
                      }}
                    >
                      {team1Players.map((player, idx) => (
                        <React.Fragment key={idx}>
                          {player.username}{idx < team1Players.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Typography>
                    {getPlayerDecks(1).map((deck, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <RaceChip race={deck.race} size="small" />
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>

          {/* VS */}
          <Typography 
            variant="body1" 
            sx={{ 
              mx: 1,
              fontSize: { xs: '0.86rem', sm: '0.99rem' },
              color: 'text.secondary',
              fontWeight: 'bold'
            }}
          >
            VS
          </Typography>

          {/* Team 2 */}
          <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              justifyContent: 'center'
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: !isTeam1Winner ? '#1976d2' : 'text.secondary',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' },
                  minWidth: '60px',
                  textAlign: 'center',
                  flexShrink: 0,
                  order: 2
                }}
              >
                {match.team2?.score || 0}
              </Typography>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 0,
                width: '100%',
                order: 1
              }}>
                <Stack spacing={0.5} alignItems="center">
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0,
                    '& .MuiBadge-root': {
                      marginLeft: '-8px',
                      '&:first-of-type': {
                        marginLeft: 0
                      }
                    }
                  }}>
                    {team2Players.map((player, idx) => (
                      <Badge
                        key={idx}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          player.approval_status === 'rejected' ? (
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #f44336'
                              }}
                            >
                              <RejectedIcon
                                sx={{
                                  fontSize: 14,
                                  color: '#f44336',
                                }}
                              />
                            </Box>
                          ) : null
                        }
                      >
                        <Avatar
                          sx={{
                            width: 26.8,
                            height: 26.8,
                            fontSize: '0.76rem',
                            bgcolor: !isTeam1Winner ? '#1976d2' : 'grey.700',
                            border: '2px solid',
                            borderColor: player.approval_status === 'rejected' ? '#f44336' : (!isTeam1Winner ? '#1976d2' : 'grey.500'),
                            color: 'white'
                          }}
                        >
                          {player.username?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                      </Badge>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={!isTeam1Winner ? 'bold' : 'normal'}
                      sx={{ 
                        fontSize: { xs: '0.735rem', sm: '0.86rem' },
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: !isTeam1Winner ? '#1976d2' : 'text.secondary'
                      }}
                    >
                      {team2Players.map((player, idx) => (
                        <React.Fragment key={idx}>
                          {player.username}{idx < team2Players.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Typography>
                    {getPlayerDecks(2).map((deck, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <RaceChip race={deck.race} size="small" />
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Match Info - Highlights */}
        {(() => {
          try {
            if (!match.highlights) return null;
            const highlights = Array.isArray(match.highlights) ? 
              match.highlights : 
              JSON.parse(match.highlights);

            if (Array.isArray(highlights) && highlights.length > 0) {
              return (
                <Stack 
                  direction="row" 
                  spacing={0.4}
                  justifyContent="center"
                  flexWrap="wrap"
                  sx={{ gap: 0.4 }}
                >
                  {highlights.map((highlight, index) => {
                    // Asegurar que highlight es un string
                    const highlightText = typeof highlight === 'string' ? highlight :
                      typeof highlight === 'object' && highlight.description ? highlight.description :
                      'Highlight desconocido';
                    
                    return (
                      <Chip
                        key={index}
                        icon={getHighlightIcon(highlightText)}
                        label={highlightText}
                        size="small"
                        sx={{ 
                          height: '22px',
                          bgcolor: '#0277bd',
                          color: 'white',
                          '& .MuiChip-label': { 
                            fontSize: '0.7rem',
                            px: 0.8
                          },
                          '& .MuiChip-icon': {
                            color: 'white',
                            fontSize: '1.2rem'
                          }
                        }}
                      />
                    );
                  })}
                </Stack>
              );
            }
            return null;
          } catch (error) {
            console.warn('Error parsing highlights:', error);
            return null;
          }
        })()}

        {/* Action Buttons */}
        <ButtonGroup 
          variant="contained" 
          sx={{ 
            width: '100%', 
            mt: 1,
            '& .MuiButton-root': {
              flex: 1,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 1,
              minWidth: 0
            }
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Button
            color="success"
            startIcon={<ApprovedIcon />}
            onClick={handleOk}
            disabled={loading}
            sx={{ 
              pointerEvents: 'auto',
              '& .MuiButton-startIcon': {
                marginRight: '2px'
              },
              '& .MuiButton-label': {
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Aceptar Rechazo'
            )}
          </Button>
          <Button
            color="primary"
            startIcon={<GameIcon />}
            onClick={handleCreateNew}
            disabled={loading}
            sx={{ pointerEvents: 'auto' }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Crear Nueva'
            )}
          </Button>
        </ButtonGroup>
      </Stack>
    </Paper>
  );
};

export default DenyMatchCard; 